import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useHistory } from "../../../shared/hooks/useHistory";
import { useDispatch, useSelector } from "react-redux";
import Iframe from "react-iframe";

import {
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import { Header } from "../../../shared/components/header";
import { learningStyles } from "./Learning.styles";
import {
  asyncGetContentById,
  asyncGetRelatedContent,
  resetCurrentContent,
} from "../../../content/actions/contentActions";
import { formattedDate } from "../../../shared/formattedDate";

import { ClockIcon } from "../../../shared/icons";
import { RootState } from "../../../store";
import { Navigation } from "../../../models/navigation";
import { ContentsById } from "../../../models/api/contentsById";
import { RecommendedContentPagination } from "../../../components/recommended-content-pagination/recommended-content-pagination.component";
import { ListContent } from "../../components/listContent";
import { PARTICIPANT_HOME_PATH } from "../../../shared/consts/participantHomePath";
import { defaultContentBackgroundImg } from "../../../shared/consts/imgUrls";

interface LearningDetailsProps {
  navigation?: Navigation[];
}

export const LearningDetails = ({ navigation }: LearningDetailsProps) => {
  const styles = learningStyles();
  const params = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const history = useHistory<{ from: string }>();
  const mainContentId = history?.location?.state?.from;
  const relatedContent = useSelector<RootState, ContentsById[]>(
    (state) => state.content.relatedContent
  );

  const relatedContentCards = relatedContent.filter(
    (content) => content.contentId !== parseInt(params.id, 10)
  );

  const editingContent = useSelector<RootState, ContentsById>(
    (state) => state.content.editingContent
  );

  useEffect(() => {
    if (params.id) {
      dispatch(asyncGetContentById(params.id));
    }
  }, [dispatch, params.id, editingContent.contentId]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (editingContent.resourceType) {
      timer = setTimeout(() => setShow(true), 1000);
    }

    return () => clearTimeout(timer);
  }, [editingContent.contentId]);

  useEffect(() => {
    dispatch(asyncGetRelatedContent(mainContentId || params.id));
  }, [dispatch]);

  const currentContentId = relatedContent?.findIndex((content) => {
    return content.contentId === parseInt(params.id, 10);
  });
  const nextContentId = relatedContent[currentContentId + 1]?.contentId;

  const prevContentId = relatedContent[currentContentId - 1]?.contentId;

  const handlePaginationClick = (contentId) => {
    dispatch(resetCurrentContent);
    history.push(`${PARTICIPANT_HOME_PATH}/learning/${contentId}`, {
      from: mainContentId,
    });
  };

  return (
    <>
      <Container>
        {show && (
          <img
            src={editingContent?.imagePath || defaultContentBackgroundImg}
            alt="Fruition"
            title="Fruition"
            className={styles.backgroundImage}
          />
        )}
        <Paper elevation={1} className={styles.paperContainer}>
          <Header navigation={navigation}>
            <div>
              <Divider />
            </div>
            {!editingContent.contentId && !show && (
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <LinearProgress color="secondary" />
              </Grid>
            )}
            {show && editingContent.resourceType && (
              <Grid container>
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  className={styles.contentDetails}
                >
                  <span className={styles.contentDetail}>
                    {editingContent.resourceType || ""}
                  </span>
                  <span className={styles.contentDetail}>
                    {editingContent.category || ""}
                  </span>
                  {editingContent.duration !== 0 && (
                    <span className={styles.contentDetail}>
                      <ClockIcon className={styles.clockIcon} />{" "}
                      {editingContent.duration || ""} minutes read
                    </span>
                  )}

                  <span className={styles.contentDetail}>
                    {editingContent.createdAt
                      ? formattedDate(editingContent.createdAt)
                      : ""}
                  </span>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  {show && editingContent.url && (
                    <Iframe
                      url={editingContent.url}
                      width="100%"
                      height="450px"
                      id="resource-iframe"
                    />
                  )}
                </Grid>
              </Grid>
            )}
          </Header>
          {relatedContent?.length > 1 ? (
            <RecommendedContentPagination
              relatedContent={relatedContent}
              handlePaginationClick={handlePaginationClick}
              currentContentId={currentContentId}
              nextContentId={nextContentId}
              prevContentId={prevContentId}
            />
          ) : null}
        </Paper>
      </Container>
      {relatedContent?.length > 1 ? (
        <div className={styles.recommendedContainer}>
          <Typography className={styles.recommendedTitle}>
            Related articles
          </Typography>
          <Grid container spacing={2}>
            <ListContent contentList={relatedContentCards} request />
          </Grid>
        </div>
      ) : null}
    </>
  );
};

LearningDetails.defaultProps = {
  navigation: [],
};
